import { debounce } from '../Utility/Debounce'
import { getElement } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

const FIELD_BUTTONS = '.search-filter__search-submit, .search-filter__search-clear'
const RESIZE_DEBOUNCE_DELAY = 150

// Sticky filter bar (>=tablet-wide, see CourseSearch.scss): toggles `is-active`
// on the header, which collapses the keyword-search row via CSS.
// `getComputedStyle(...).position` mirrors the breakpoint gate in CSS instead
// of duplicating it here — below tablet-wide the header stays `static`, so
// `isActive` is always false and nothing happens.
//
// The trigger point is measured ONCE (`naturalTop`, the header's expanded,
// unstuck position in document coordinates) instead of re-reading the header's
// current `getBoundingClientRect()` on every scroll tick. The collapse itself
// shrinks the header (and therefore the page's total scrollable height), so a
// live re-measurement would feed back into its own trigger condition — toggle
// active, page shrinks, scroll position gets clamped, toggle back inactive,
// page grows again, repeat — instead of settling. A cached, fixed reference
// point breaks that loop.
//
// Triggered `COLLAPSE_LEAD` px before the header is actually stuck: collapsing
// exactly at the stuck point shrinks the header's flow height right as it
// "catches", so the result list below jumps at the exact moment the eye
// expects things to settle. Starting the collapse slightly earlier lets it
// finish while the page is still actively scrolling past, so the height
// change reads as part of that motion instead of a separate glitch.
class CourseFilterSticky {
  private static readonly COLLAPSE_LEAD = 80
  private static readonly NO_TRANSITION = 'is-notransition'

  private readonly header: HTMLElement | null
  private isActive: boolean = false
  private naturalTop: number = 0

  constructor() {
    this.header = getElement('search-page-header')
  }

  public init(): void {
    if (this.header === null) {
      return
    }

    this.measureNaturalTop()
    this.applyInitialState()
    this.addEvents()
    this.keepFieldButtonFocus()
  }

  private addEvents(): void {
    window.addEventListener('scroll', (): void => this.update(), { passive: true })
    window.addEventListener('resize', debounce((): void => {
      if (!this.isActive) {
        this.measureNaturalTop()
      }
      this.update()
    }, RESIZE_DEBOUNCE_DELAY), { passive: true })
  }

  // A page can load already scrolled past the collapse threshold — e.g. the
  // browser restores the previous scroll position across a same-URL search-form
  // submission. Left to the normal transition, the header would paint fully
  // expanded for a frame and only then visibly animate into the collapsed
  // state, reading as a jump rather than the page simply settling. Suppressing
  // the transition for this one-off initial sync makes "collapsed" just the
  // starting state; live scroll-triggered collapses below still animate.
  private applyInitialState(): void {
    if (this.header === null) {
      return
    }

    this.header.classList.add(CourseFilterSticky.NO_TRANSITION)
    this.update()
    // Forces layout so the collapsed state is committed before transitions are
    // re-enabled — otherwise the class removal could land in the same frame
    // and the collapse would still animate.
    void this.header.offsetHeight
    this.header.classList.remove(CourseFilterSticky.NO_TRANSITION)
  }

  // The magnifier and the clear button both sit inside the keyword row, which
  // collapses the moment focus leaves it (`:focus-within`, CourseSearch.scss).
  // Left to the default mousedown focus shift, pressing either one collapses
  // the row — and the button under the cursor with it — while the click is still
  // being made, and the submit that follows then has no focused field left to
  // hand back to. Keeping focus in the input makes a mouse submit behave exactly
  // like Enter. Delegated on `document`: the buttons live inside the region
  // CourseFilterLive swaps out on every search.
  private keepFieldButtonFocus(): void {
    document.addEventListener('mousedown', (event: MouseEvent): void => {
      const target = event.target as Element | null
      if (!target?.closest(FIELD_BUTTONS)) {
        return
      }

      event.preventDefault()
    })
  }

  private measureNaturalTop(): void {
    if (this.header === null) {
      return
    }

    this.naturalTop = this.header.getBoundingClientRect().top + window.scrollY
  }

  private update(): void {
    if (this.header === null) {
      return
    }

    const style = getComputedStyle(this.header)
    const stickyTop = parseFloat(style.top) || 0
    const isSticky = style.position === 'sticky'
    const isActive = isSticky && window.scrollY >= this.naturalTop - stickyTop - CourseFilterSticky.COLLAPSE_LEAD

    if (isActive === this.isActive) {
      return
    }

    this.isActive = isActive
    this.header.classList.toggle(IS_ACTIVE, isActive)
  }
}

const courseFilterSticky = new CourseFilterSticky()
courseFilterSticky.init()
